// Arena land on the wire (protocol §5.4 ARENA_LAND, ARENA_MINIMAP): per-chunk run-length
// encoding of the ownership grid and the downsampled minimap, shared by the server (encode, from
// the sim's slot-valued `owner` array) and the client (decode into a player-id grid).
//
// - The sim stores ArenaPlayer.slot values (u16, reused); the wire carries player ids. Encoders
//   take `slotIds`: `slotIds[slot]` = the player id holding that slot (0 or missing = neutral).
// - A chunk is 16 × 16 cells (ARENA_NET.chunkSize), row-major over the full 16 × 16; cells past
//   the grid edge are neutral. Runs are flat `[length, playerId, …]` summing to 256.
// - The minimap is `size × size` blocks; block `(bx, by)` covers cells
//   `[floor(bx·cols/size), floor((bx+1)·cols/size))` (rows likewise) and shows the owner holding
//   most of its playable cells (neutral counts as a candidate; on a tie, the owner that reached
//   the top count first in row-major order). A block without playable cells is neutral.
//
// Pure. Owner: lane P.

import { ARENA_NET } from './constants.js';
import { chunksAlong } from './grid.js';

const CHUNK = ARENA_NET.chunkSize;
const CHUNK_CELLS = CHUNK * CHUNK;

function idOfSlot(slotIds: ArrayLike<number>, slot: number): number {
  if (slot === 0) return 0;
  const id = slotIds[slot];
  return id === undefined ? 0 : id;
}

/** Appends `[length, value, …]` runs of `values[start … end)` to `out`. */
export function appendRuns(
  values: ArrayLike<number>,
  start: number,
  end: number,
  out: number[],
): void {
  if (end <= start) return;
  let cur = values[start] as number;
  let len = 1;
  for (let i = start + 1; i < end; i++) {
    const v = values[i] as number;
    if (v === cur) {
      len++;
    } else {
      out.push(len, cur);
      cur = v;
      len = 1;
    }
  }
  out.push(len, cur);
}

/**
 * Appends chunk `chunk`'s runs (player ids, 256 cells) to `out`, reading the slot grid `owner`
 * (`cols × rows`, index `cy · cols + cx`).
 */
export function chunkRunsInto(
  owner: ArrayLike<number>,
  cols: number,
  rows: number,
  chunk: number,
  slotIds: ArrayLike<number>,
  out: number[],
): void {
  const kx = chunk % chunksAlong(cols);
  const ky = Math.floor(chunk / chunksAlong(cols));
  const x0 = kx * CHUNK;
  const y0 = ky * CHUNK;
  let cur = -1;
  let len = 0;
  for (let dy = 0; dy < CHUNK; dy++) {
    const cy = y0 + dy;
    for (let dx = 0; dx < CHUNK; dx++) {
      const cx = x0 + dx;
      const v = cx < cols && cy < rows ? idOfSlot(slotIds, owner[cy * cols + cx] as number) : 0;
      if (v === cur) {
        len++;
      } else {
        if (len > 0) out.push(len, cur);
        cur = v;
        len = 1;
      }
    }
  }
  out.push(len, cur);
}

/**
 * The client side of one ARENA_LAND chunk: writes its runs into `out` (a player-id grid of
 * `cols × rows`), skipping cells past the grid edge. Throws RangeError if the runs do not sum to
 * 256 (the reader already rejects that).
 */
export function applyChunkRuns(
  runs: readonly number[],
  chunk: number,
  cols: number,
  rows: number,
  out: { [i: number]: number; readonly length: number },
): void {
  const kx = chunk % chunksAlong(cols);
  const ky = Math.floor(chunk / chunksAlong(cols));
  const x0 = kx * CHUNK;
  const y0 = ky * CHUNK;
  let k = 0;
  for (let i = 0; i + 1 < runs.length; i += 2) {
    const len = runs[i] as number;
    const id = runs[i + 1] as number;
    for (let j = 0; j < len; j++, k++) {
      if (k >= CHUNK_CELLS) throw new RangeError('chunk runs exceed 256 cells');
      const cx = x0 + (k % CHUNK);
      const cy = y0 + Math.floor(k / CHUNK);
      if (cx < cols && cy < rows) out[cy * cols + cx] = id;
    }
  }
  if (k !== CHUNK_CELLS) throw new RangeError(`chunk runs cover ${k} cells, not 256`);
}

/** The chunks whose cells meet the cell rectangle `[x0, x1] × [y0, y1]` (inclusive, clipped). */
export function chunkRangeOfCells(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  cols: number,
  rows: number,
): { kx0: number; ky0: number; kx1: number; ky1: number } {
  const maxKx = chunksAlong(cols) - 1;
  const maxKy = chunksAlong(rows) - 1;
  const clamp = (v: number, hi: number): number => (v < 0 ? 0 : v > hi ? hi : v);
  return {
    kx0: clamp(Math.floor(x0 / CHUNK), maxKx),
    ky0: clamp(Math.floor(y0 / CHUNK), maxKy),
    kx1: clamp(Math.floor(x1 / CHUNK), maxKx),
    ky1: clamp(Math.floor(y1 / CHUNK), maxKy),
  };
}

/** Scratch for `minimapRunsInto` (per-slot counts); reuse one per caller. */
export interface MinimapScratch {
  counts: Uint32Array;
  touched: number[];
}

export function createMinimapScratch(): MinimapScratch {
  return { counts: new Uint32Array(0x10000), touched: [] };
}

/**
 * Appends the minimap's runs (`size × size` blocks, row-major, player ids) to `out`. `playable`
 * (1 = inside the mask) may be null for "every cell".
 */
export function minimapRunsInto(
  owner: ArrayLike<number>,
  playable: ArrayLike<number> | null,
  cols: number,
  rows: number,
  size: number,
  slotIds: ArrayLike<number>,
  out: number[],
  scratch: MinimapScratch = createMinimapScratch(),
): void {
  const { counts, touched } = scratch;
  let cur = -1;
  let len = 0;
  for (let by = 0; by < size; by++) {
    const y0 = Math.floor((by * rows) / size);
    const y1 = Math.floor(((by + 1) * rows) / size);
    for (let bx = 0; bx < size; bx++) {
      const x0 = Math.floor((bx * cols) / size);
      const x1 = Math.floor(((bx + 1) * cols) / size);
      let best = 0;
      let bestCount = 0;
      for (let cy = y0; cy < y1; cy++) {
        const row = cy * cols;
        for (let cx = x0; cx < x1; cx++) {
          const i = row + cx;
          if (playable !== null && playable[i] !== 1) continue;
          const s = owner[i] as number;
          const c = counts[s] as number;
          if (c === 0) touched.push(s);
          counts[s] = c + 1;
          if (c + 1 > bestCount) {
            bestCount = c + 1;
            best = s;
          }
        }
      }
      for (const s of touched) counts[s] = 0;
      touched.length = 0;
      const v = idOfSlot(slotIds, best);
      if (v === cur) {
        len++;
      } else {
        if (len > 0) out.push(len, cur);
        cur = v;
        len = 1;
      }
    }
  }
  if (len > 0) out.push(len, cur);
}

/** A head's minimap cell along one axis: `floor(pos · size / side)`, clamped to `[0, size)`. */
export function minimapCoord(pos: number, side: number, size: number): number {
  const v = Math.floor((pos * size) / side);
  return v < 0 ? 0 : v >= size ? size - 1 : v;
}
